import { Purchases } from "@revenuecat/purchases-js";

export type RevenueCatCourseLevel = "A1" | "A2" | "B1" | "B2";
export type CourseAccessLevel = "A1" | "A2" | "B1" | "B2" | "C1" | "C2";

export type CourseAccessMap = Record<CourseAccessLevel, boolean> & {
  full: boolean;
};

export type BookAccessMap = Record<CourseAccessLevel, boolean> & {
  full: boolean;
};

type PurchaseCourseParams = {
  appUserId: string;
  courseLevel: RevenueCatCourseLevel;
  email?: string | null;
};

type PurchaseWebsitePremiumParams = {
  appUserId: string;
  email?: string | null;
};

type RevenueCatCustomerInfo = {
  entitlements?: {
    active?: Record<string, unknown>;
  };
};

type RevenueCatPurchaseResult = {
  customerInfo?: RevenueCatCustomerInfo;
} & RevenueCatCustomerInfo;

type RevenueCatPackage = {
  identifier?: string;
  packageType?: string;
  rcBillingProduct?: {
    identifier?: string;
    id?: string;
  };
  webBillingProduct?: {
    identifier?: string;
    id?: string;
  };
  product?: {
    identifier?: string;
    id?: string;
  };
};

type RevenueCatOffering = {
  identifier?: string;
  availablePackages?: RevenueCatPackage[];
  packagesById?: Record<string, RevenueCatPackage>;
};

type RevenueCatOfferings = {
  current?: RevenueCatOffering | null;
  all?: Record<string, RevenueCatOffering>;
};

type BackendAccessResponse = {
  email: string;
  has_record: boolean;
  is_active: boolean;

  course_access_levels: string[];
  book_access_levels: string[];

  has_full_access: boolean;
  has_full_course_access: boolean;
  has_full_book_access: boolean;

  access_map: CourseAccessMap;
  course_access_map: CourseAccessMap;
  book_access_map: BookAccessMap;

  accessible_levels: CourseAccessLevel[];
  accessible_course_levels: CourseAccessLevel[];
  accessible_book_levels: CourseAccessLevel[];

  requested_course_level: CourseAccessLevel | null;
  can_access_requested_level: boolean;
  can_access_requested_course_level: boolean;

  requested_book_level: CourseAccessLevel | null;
  can_access_requested_book_level: boolean;
};

type CachedBackendAccessResponse = {
  saved_at: number;
  data: BackendAccessResponse;
};

export const REVENUECAT_COURSE_LEVELS: RevenueCatCourseLevel[] = [
  "A1",
  "A2",
  "B1",
  "B2",
];

export const COURSE_ACCESS_LEVELS: CourseAccessLevel[] = [
  "A1",
  "A2",
  "B1",
  "B2",
  "C1",
  "C2",
];

const API_BASE_URL =
  process.env.REACT_APP_BACKEND_API_URL?.trim() ||
  process.env.REACT_APP_API_BASE_URL?.trim() ||
  "https://www.api.dearlanguage.com";

const WEB_BILLING_API_KEY =
  process.env.REACT_APP_REVENUECAT_WEB_API_KEY?.trim() ?? "";

const OFFERING_ID =
  process.env.REACT_APP_REVENUECAT_OFFERING_ID?.trim() || "DeutschKurse";

const FULL_WEBSITE_PACKAGE_ID =
  process.env.REACT_APP_REVENUECAT_FULL_WEBSITE_PACKAGE_ID?.trim() ||
  "website_full_month";

const FULL_WEBSITE_ENTITLEMENT_ID =
  process.env.REACT_APP_REVENUECAT_FULL_WEBSITE_ENTITLEMENT_ID?.trim() ||
  "website_full_access";

const PACKAGE_IDS: Record<RevenueCatCourseLevel, string> = {
  A1:
    process.env.REACT_APP_REVENUECAT_A1_PACKAGE_ID?.trim() ||
    "a1_Deutschkurs",
  A2:
    process.env.REACT_APP_REVENUECAT_A2_PACKAGE_ID?.trim() ||
    "a2_Deutschkurs",
  B1:
    process.env.REACT_APP_REVENUECAT_B1_PACKAGE_ID?.trim() ||
    "b1_Deutschkurs",
  B2:
    process.env.REACT_APP_REVENUECAT_B2_PACKAGE_ID?.trim() ||
    "b2_Deutschkurs",
};

const ENTITLEMENT_IDS: Record<RevenueCatCourseLevel, string> = {
  A1: process.env.REACT_APP_REVENUECAT_A1_ENTITLEMENT_ID?.trim() || "course_a1",
  A2: process.env.REACT_APP_REVENUECAT_A2_ENTITLEMENT_ID?.trim() || "course_a2",
  B1: process.env.REACT_APP_REVENUECAT_B1_ENTITLEMENT_ID?.trim() || "course_b1",
  B2: process.env.REACT_APP_REVENUECAT_B2_ENTITLEMENT_ID?.trim() || "course_b2",
};

let configuredAppUserId: string | null = null;

const backendAccessCache = new Map<string, BackendAccessResponse>();
const backendAccessCacheTime = new Map<string, number>();

const BACKEND_ACCESS_CACHE_PREFIX = "dear-language-backend-access-v4";
const BACKEND_ACCESS_CACHE_TIME_MS = 1000 * 60 * 60 * 12;

const safeGetSessionStorageItem = (key: string): string | null => {
  try {
    if (typeof window === "undefined" || !window.sessionStorage) {
      return null;
    }

    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

const safeSetSessionStorageItem = (key: string, value: string): void => {
  try {
    if (typeof window === "undefined" || !window.sessionStorage) {
      return;
    }

    window.sessionStorage.setItem(key, value);
  } catch {
    // Session cache is optional.
  }
};

const safeRemoveSessionStorageItem = (key: string): void => {
  try {
    if (typeof window === "undefined" || !window.sessionStorage) {
      return;
    }

    window.sessionStorage.removeItem(key);
  } catch {
    // Session cache is optional.
  }
};

const getCleanApiBaseUrl = (): string => {
  return API_BASE_URL.replace(/\/+$/, "");
};

const getCleanEmail = (email?: string | null): string => {
  return email?.trim().toLowerCase() ?? "";
};

const isRevenueCatCourseLevel = (
  level: string,
): level is RevenueCatCourseLevel => {
  return level === "A1" || level === "A2" || level === "B1" || level === "B2";
};

const isCourseAccessLevel = (level: string): level is CourseAccessLevel => {
  return (
    level === "A1" ||
    level === "A2" ||
    level === "B1" ||
    level === "B2" ||
    level === "C1" ||
    level === "C2"
  );
};

const normalizeCourseAccessLevel = (
  level?: string | null,
): CourseAccessLevel | null => {
  const cleanLevel = String(level || "").trim().toUpperCase();

  return isCourseAccessLevel(cleanLevel) ? cleanLevel : null;
};

const createAccessMap = (value: boolean): CourseAccessMap => ({
  full: value,
  A1: value,
  A2: value,
  B1: value,
  B2: value,
  C1: value,
  C2: value,
});

const mergeAccessMaps = (
  first: CourseAccessMap,
  second: CourseAccessMap,
): CourseAccessMap => {
  if (first.full || second.full) {
    return createAccessMap(true);
  }

  return {
    full: false,
    A1: Boolean(first.A1 || second.A1),
    A2: Boolean(first.A2 || second.A2),
    B1: Boolean(first.B1 || second.B1),
    B2: Boolean(first.B2 || second.B2),
    C1: Boolean(first.C1 || second.C1),
    C2: Boolean(first.C2 || second.C2),
  };
};

const getAccessibleLevelsFromMap = (map: CourseAccessMap): CourseAccessLevel[] =>
  COURSE_ACCESS_LEVELS.filter((level) => Boolean(map[level]));

const createNoAccessResponse = (
  email?: string | null,
  requestedCourseLevel?: CourseAccessLevel | null,
  requestedBookLevel?: CourseAccessLevel | null,
): BackendAccessResponse => {
  const emptyMap = createAccessMap(false);

  return {
    email: getCleanEmail(email),
    has_record: false,
    is_active: false,

    course_access_levels: [],
    book_access_levels: [],

    has_full_access: false,
    has_full_course_access: false,
    has_full_book_access: false,

    access_map: emptyMap,
    course_access_map: emptyMap,
    book_access_map: emptyMap,

    accessible_levels: [],
    accessible_course_levels: [],
    accessible_book_levels: [],

    requested_course_level: requestedCourseLevel ?? null,
    can_access_requested_level: false,
    can_access_requested_course_level: false,

    requested_book_level: requestedBookLevel ?? null,
    can_access_requested_book_level: false,
  };
};

const normalizeMap = (rawMap: unknown, hasFull: boolean): CourseAccessMap => {
  const mapValue = rawMap as Partial<CourseAccessMap> | null | undefined;

  const accessMap: CourseAccessMap = {
    full: Boolean(mapValue?.full ?? hasFull),
    A1: Boolean(mapValue?.A1),
    A2: Boolean(mapValue?.A2),
    B1: Boolean(mapValue?.B1),
    B2: Boolean(mapValue?.B2),
    C1: Boolean(mapValue?.C1),
    C2: Boolean(mapValue?.C2),
  };

  if (accessMap.full || hasFull) {
    return createAccessMap(true);
  }

  return accessMap;
};

const normalizeStringArray = (value: unknown): string[] => {
  return Array.isArray(value) ? value.map((item) => String(item)) : [];
};

const normalizeLevelArray = (value: unknown): CourseAccessLevel[] => {
  return Array.isArray(value)
    ? value
        .map((item) => normalizeCourseAccessLevel(String(item)))
        .filter((item): item is CourseAccessLevel => Boolean(item))
    : [];
};

const normalizeBackendAccessResponse = (
  raw: unknown,
  email?: string | null,
  requestedCourseLevel?: CourseAccessLevel | null,
  requestedBookLevel?: CourseAccessLevel | null,
): BackendAccessResponse => {
  const data = raw as Record<string, unknown> | null | undefined;

  if (!data || typeof data !== "object") {
    return createNoAccessResponse(
      email,
      requestedCourseLevel,
      requestedBookLevel,
    );
  }

  const courseAccessMap = normalizeMap(
    data.course_access_map || data.access_map,
    Boolean(data.has_full_course_access || data.has_full_access),
  );

  const bookAccessMap = normalizeMap(
    data.book_access_map,
    Boolean(data.has_full_book_access || data.has_full_access),
  );

  const accessibleCourseLevels =
    normalizeLevelArray(data.accessible_course_levels).length > 0
      ? normalizeLevelArray(data.accessible_course_levels)
      : normalizeLevelArray(data.accessible_levels).length > 0
        ? normalizeLevelArray(data.accessible_levels)
        : getAccessibleLevelsFromMap(courseAccessMap);

  const accessibleBookLevels =
    normalizeLevelArray(data.accessible_book_levels).length > 0
      ? normalizeLevelArray(data.accessible_book_levels)
      : getAccessibleLevelsFromMap(bookAccessMap);

  const normalizedRequestedCourseLevel =
    normalizeCourseAccessLevel(String(data.requested_course_level || "")) ||
    requestedCourseLevel ||
    null;

  const normalizedRequestedBookLevel =
    normalizeCourseAccessLevel(String(data.requested_book_level || "")) ||
    requestedBookLevel ||
    null;

  const courseAccessLevels =
    normalizeStringArray(data.course_access_levels).length > 0
      ? normalizeStringArray(data.course_access_levels)
      : normalizeStringArray(data.access_levels);

  return {
    email: getCleanEmail(String(data.email || email || "")),
    has_record: Boolean(data.has_record),
    is_active: Boolean(data.is_active),

    course_access_levels: courseAccessLevels,
    book_access_levels: normalizeStringArray(data.book_access_levels),

    has_full_access: Boolean(data.has_full_access || courseAccessMap.full),
    has_full_course_access: Boolean(
      data.has_full_course_access || data.has_full_access || courseAccessMap.full,
    ),
    has_full_book_access: Boolean(
      data.has_full_book_access || bookAccessMap.full,
    ),

    access_map: courseAccessMap,
    course_access_map: courseAccessMap,
    book_access_map: bookAccessMap,

    accessible_levels: accessibleCourseLevels,
    accessible_course_levels: accessibleCourseLevels,
    accessible_book_levels: accessibleBookLevels,

    requested_course_level: normalizedRequestedCourseLevel,
    can_access_requested_level: normalizedRequestedCourseLevel
      ? Boolean(courseAccessMap[normalizedRequestedCourseLevel])
      : Boolean(data.can_access_requested_level),
    can_access_requested_course_level: normalizedRequestedCourseLevel
      ? Boolean(courseAccessMap[normalizedRequestedCourseLevel])
      : Boolean(data.can_access_requested_course_level),

    requested_book_level: normalizedRequestedBookLevel,
    can_access_requested_book_level: normalizedRequestedBookLevel
      ? Boolean(bookAccessMap[normalizedRequestedBookLevel])
      : Boolean(data.can_access_requested_book_level),
  };
};

const getBackendCacheKey = (
  email: string,
  courseLevel?: CourseAccessLevel | null,
  bookLevel?: CourseAccessLevel | null,
): string => {
  return `${email}::course:${courseLevel || "all"}::book:${bookLevel || "all"}`;
};

const getBackendSessionStorageKey = (cacheKey: string): string => {
  return `${BACKEND_ACCESS_CACHE_PREFIX}:${cacheKey}`;
};

const getCachedBackendAccess = (
  email?: string | null,
  courseLevel?: CourseAccessLevel | null,
  bookLevel?: CourseAccessLevel | null,
): BackendAccessResponse | null => {
  const cleanEmail = getCleanEmail(email);

  if (!cleanEmail) {
    return null;
  }

  const cacheKey = getBackendCacheKey(cleanEmail, courseLevel, bookLevel);
  const storageKey = getBackendSessionStorageKey(cacheKey);

  const cached = backendAccessCache.get(cacheKey);
  const cachedAt = backendAccessCacheTime.get(cacheKey) || 0;

  if (cached && Date.now() - cachedAt <= BACKEND_ACCESS_CACHE_TIME_MS) {
    return cached;
  }

  if (cached) {
    backendAccessCache.delete(cacheKey);
    backendAccessCacheTime.delete(cacheKey);
    safeRemoveSessionStorageItem(storageKey);
  }

  try {
    const stored = safeGetSessionStorageItem(storageKey);

    if (!stored) {
      return null;
    }

    const parsed = JSON.parse(stored) as CachedBackendAccessResponse;

    if (!parsed?.data || !parsed?.saved_at) {
      safeRemoveSessionStorageItem(storageKey);
      return null;
    }

    if (Date.now() - parsed.saved_at > BACKEND_ACCESS_CACHE_TIME_MS) {
      safeRemoveSessionStorageItem(storageKey);
      return null;
    }

    const normalized = normalizeBackendAccessResponse(
      parsed.data,
      cleanEmail,
      courseLevel,
      bookLevel,
    );

    backendAccessCache.set(cacheKey, normalized);
    backendAccessCacheTime.set(cacheKey, parsed.saved_at);

    return normalized;
  } catch {
    safeRemoveSessionStorageItem(storageKey);
    return null;
  }
};

const setSingleCachedBackendAccess = (
  cacheKey: string,
  value: BackendAccessResponse,
): void => {
  const savedAt = Date.now();
  const storageKey = getBackendSessionStorageKey(cacheKey);

  backendAccessCache.set(cacheKey, value);
  backendAccessCacheTime.set(cacheKey, savedAt);

  const storedValue: CachedBackendAccessResponse = {
    saved_at: savedAt,
    data: value,
  };

  safeSetSessionStorageItem(storageKey, JSON.stringify(storedValue));
};

const setCachedBackendAccess = (
  email: string,
  courseLevel: CourseAccessLevel | null,
  bookLevel: CourseAccessLevel | null,
  value: BackendAccessResponse,
): void => {
  const cacheKey = getBackendCacheKey(email, courseLevel, bookLevel);

  setSingleCachedBackendAccess(cacheKey, value);

  const allCacheKey = getBackendCacheKey(email, null, null);

  setSingleCachedBackendAccess(allCacheKey, {
    ...value,
    requested_course_level: null,
    can_access_requested_level: false,
    can_access_requested_course_level: false,
    requested_book_level: null,
    can_access_requested_book_level: false,
  });
};

export const clearBackendCourseAccessCache = (): void => {
  backendAccessCache.clear();
  backendAccessCacheTime.clear();

  try {
    if (typeof window === "undefined" || !window.sessionStorage) {
      return;
    }

    Object.keys(window.sessionStorage).forEach((key) => {
      if (key.startsWith(`${BACKEND_ACCESS_CACHE_PREFIX}:`)) {
        window.sessionStorage.removeItem(key);
      }
    });
  } catch {
    // Session cache is optional.
  }
};

export const getBackendCourseAccess = async (
  email?: string | null,
  courseLevel?: CourseAccessLevel | null,
  forceRefresh: boolean = false,
  bookLevel?: CourseAccessLevel | null,
): Promise<BackendAccessResponse> => {
  const cleanEmail = getCleanEmail(email);
  const normalizedCourseLevel = normalizeCourseAccessLevel(courseLevel || null);
  const normalizedBookLevel = normalizeCourseAccessLevel(bookLevel || null);

  if (!cleanEmail) {
    return createNoAccessResponse(
      cleanEmail,
      normalizedCourseLevel,
      normalizedBookLevel,
    );
  }

  if (!forceRefresh) {
    const cached = getCachedBackendAccess(
      cleanEmail,
      normalizedCourseLevel,
      normalizedBookLevel,
    );

    if (cached) {
      return cached;
    }
  }

  const searchParams = new URLSearchParams({
    email: cleanEmail,
  });

  if (normalizedCourseLevel) {
    searchParams.set("course_level", normalizedCourseLevel);
  }

  if (normalizedBookLevel) {
    searchParams.set("book_level", normalizedBookLevel);
  }

  const response = await fetch(
    `${getCleanApiBaseUrl()}/api/access/check?${searchParams.toString()}`,
    {
      method: "GET",
      cache: "no-store",
      headers: {
        Accept: "application/json",
      },
    },
  );

  if (!response.ok) {
    throw new Error(
      `Backend-Zugang konnte nicht geprüft werden. Status: ${response.status}`,
    );
  }

  const json = await response.json();
  const normalized = normalizeBackendAccessResponse(
    json,
    cleanEmail,
    normalizedCourseLevel,
    normalizedBookLevel,
  );

  setCachedBackendAccess(
    cleanEmail,
    normalizedCourseLevel,
    normalizedBookLevel,
    normalized,
  );

  return normalized;
};

export const refreshBackendCourseAccess = async (
  email?: string | null,
  courseLevel?: CourseAccessLevel | null,
  bookLevel?: CourseAccessLevel | null,
): Promise<BackendAccessResponse> => {
  return getBackendCourseAccess(email, courseLevel, true, bookLevel);
};

export const isFullAccessEmail = (email?: string | null): boolean => {
  const cleanEmail = getCleanEmail(email);

  if (!cleanEmail) {
    return false;
  }

  const cached = getCachedBackendAccess(cleanEmail, null, null);

  return Boolean(cached?.has_full_access || cached?.has_full_course_access);
};

export const isFullBookAccessEmail = (email?: string | null): boolean => {
  const cleanEmail = getCleanEmail(email);

  if (!cleanEmail) {
    return false;
  }

  const cached = getCachedBackendAccess(cleanEmail, null, null);

  return Boolean(cached?.has_full_book_access);
};

export const getBackendCourseAccessMap = async (
  email?: string | null,
): Promise<CourseAccessMap> => {
  const backendAccess = await getBackendCourseAccess(email);

  return backendAccess.course_access_map;
};

export const getBackendBookAccessMap = async (
  email?: string | null,
): Promise<BookAccessMap> => {
  const backendAccess = await getBackendCourseAccess(email);

  return backendAccess.book_access_map;
};

export const checkBackendCourseAccess = async (
  email?: string | null,
  courseLevel?: CourseAccessLevel | null,
): Promise<boolean> => {
  const normalizedCourseLevel = normalizeCourseAccessLevel(courseLevel || null);

  if (!normalizedCourseLevel) {
    return false;
  }

  const backendAccess = await getBackendCourseAccess(
    email,
    normalizedCourseLevel,
  );

  return Boolean(backendAccess.course_access_map[normalizedCourseLevel]);
};

export const checkBackendBookAccess = async (
  email?: string | null,
  bookLevel?: CourseAccessLevel | null,
): Promise<boolean> => {
  const normalizedBookLevel = normalizeCourseAccessLevel(bookLevel || null);

  if (!normalizedBookLevel) {
    return false;
  }

  const backendAccess = await getBackendCourseAccess(
    email,
    null,
    false,
    normalizedBookLevel,
  );

  return Boolean(backendAccess.book_access_map[normalizedBookLevel]);
};

const getCleanAppUserId = (appUserId: string): string => {
  const cleanAppUserId = appUserId.trim();

  if (!cleanAppUserId) {
    throw new Error("Firebase User-ID fehlt. Bitte loggen Sie sich erneut ein.");
  }

  return cleanAppUserId;
};

const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  if (typeof error === "string") {
    return error;
  }

  try {
    return JSON.stringify(error);
  } catch {
    return "Unbekannter Fehler.";
  }
};

const validateRevenueCatKey = (): void => {
  if (!WEB_BILLING_API_KEY) {
    throw new Error(
      "REACT_APP_REVENUECAT_WEB_API_KEY fehlt. Bitte tragen Sie den RevenueCat Web Billing API-Key in .env ein und starten Sie npm neu.",
    );
  }

  if (WEB_BILLING_API_KEY.includes("PASTE_")) {
    throw new Error(
      "Der RevenueCat API-Key ist noch ein Platzhalter. Bitte verwenden Sie den echten RevenueCat Web Billing API-Key.",
    );
  }

  if (
    WEB_BILLING_API_KEY.includes("sk_") ||
    WEB_BILLING_API_KEY.includes("pk_") ||
    WEB_BILLING_API_KEY.toLowerCase().includes("stripe")
  ) {
    throw new Error(
      "Sie verwenden wahrscheinlich einen Stripe-Key. Hier muss der RevenueCat Web Billing API-Key stehen.",
    );
  }
};

const getPurchasesClass = (): any => {
  return Purchases as any;
};

export const configureRevenueCatWeb = async (
  appUserId: string,
): Promise<any> => {
  const cleanAppUserId = getCleanAppUserId(appUserId);

  validateRevenueCatKey();

  const PurchasesClass = getPurchasesClass();

  try {
    if (
      typeof PurchasesClass.isConfigured === "function" &&
      PurchasesClass.isConfigured()
    ) {
      const sharedInstance = PurchasesClass.getSharedInstance();

      if (
        configuredAppUserId &&
        configuredAppUserId !== cleanAppUserId &&
        typeof sharedInstance.changeUser === "function"
      ) {
        await sharedInstance.changeUser(cleanAppUserId);
      }

      configuredAppUserId = cleanAppUserId;

      return sharedInstance;
    }

    let purchasesInstance: any;

    try {
      purchasesInstance = PurchasesClass.configure({
        apiKey: WEB_BILLING_API_KEY,
        appUserId: cleanAppUserId,
      });
    } catch {
      purchasesInstance = PurchasesClass.configure(
        WEB_BILLING_API_KEY,
        cleanAppUserId,
      );
    }

    configuredAppUserId = cleanAppUserId;

    if (purchasesInstance && typeof purchasesInstance.preload === "function") {
      try {
        await purchasesInstance.preload();
      } catch {
        // Preload is optional.
      }
    }

    return purchasesInstance;
  } catch (error) {
    throw new Error(
      `RevenueCat konnte nicht gestartet werden: ${getErrorMessage(error)}`,
    );
  }
};

const getOfferings = async (purchases: any): Promise<RevenueCatOfferings> => {
  if (!purchases || typeof purchases.getOfferings !== "function") {
    throw new Error("RevenueCat wurde nicht richtig gestartet.");
  }

  try {
    return await purchases.getOfferings({
      currency: "EUR",
    });
  } catch {
    return await purchases.getOfferings();
  }
};

const getAllPackagesFromOffering = (
  offering?: RevenueCatOffering | null,
): RevenueCatPackage[] => {
  if (!offering) {
    return [];
  }

  const fromAvailablePackages = Array.isArray(offering.availablePackages)
    ? offering.availablePackages
    : [];

  const fromPackagesById = offering.packagesById
    ? Object.values(offering.packagesById)
    : [];

  const combinedPackages = [...fromAvailablePackages, ...fromPackagesById];

  return combinedPackages.filter((rcPackage, index, array) => {
    const id =
      rcPackage?.identifier ||
      rcPackage?.rcBillingProduct?.identifier ||
      rcPackage?.rcBillingProduct?.id ||
      rcPackage?.webBillingProduct?.identifier ||
      rcPackage?.webBillingProduct?.id ||
      rcPackage?.product?.identifier ||
      rcPackage?.product?.id ||
      String(index);

    return (
      array.findIndex((item, itemIndex) => {
        const itemId =
          item?.identifier ||
          item?.rcBillingProduct?.identifier ||
          item?.rcBillingProduct?.id ||
          item?.webBillingProduct?.identifier ||
          item?.webBillingProduct?.id ||
          item?.product?.identifier ||
          item?.product?.id ||
          String(itemIndex);

        return itemId === id;
      }) === index
    );
  });
};

const packageMatchesIdentifier = (
  rcPackage: RevenueCatPackage,
  targetPackageId: string,
): boolean => {
  const possibleIds = [
    rcPackage?.identifier,
    rcPackage?.rcBillingProduct?.identifier,
    rcPackage?.rcBillingProduct?.id,
    rcPackage?.webBillingProduct?.identifier,
    rcPackage?.webBillingProduct?.id,
    rcPackage?.product?.identifier,
    rcPackage?.product?.id,
  ]
    .filter(Boolean)
    .map((id) => String(id));

  return possibleIds.some((id) => id === targetPackageId);
};

const findPackage = async (
  purchases: any,
  packageId: string,
): Promise<RevenueCatPackage> => {
  const offerings = await getOfferings(purchases);
  const offering =
    offerings.all?.[OFFERING_ID] ||
    offerings.current ||
    Object.values(offerings.all || {})[0];

  const packages = getAllPackagesFromOffering(offering);
  const foundPackage = packages.find((rcPackage) =>
    packageMatchesIdentifier(rcPackage, packageId),
  );

  if (!foundPackage) {
    throw new Error(
      `RevenueCat Produkt wurde nicht gefunden: ${packageId}. Bitte prüfen Sie Offering "${OFFERING_ID}".`,
    );
  }

  return foundPackage;
};

const findCoursePackage = async (
  purchases: any,
  courseLevel: RevenueCatCourseLevel,
): Promise<RevenueCatPackage> => {
  return findPackage(purchases, PACKAGE_IDS[courseLevel]);
};

const findWebsitePremiumPackage = async (
  purchases: any,
): Promise<RevenueCatPackage> => {
  return findPackage(purchases, FULL_WEBSITE_PACKAGE_ID);
};

const normalizeCustomerInfo = (
  purchaseResult: RevenueCatPurchaseResult,
): RevenueCatCustomerInfo => {
  return purchaseResult?.customerInfo || purchaseResult;
};

const purchasePackageAndReturnCustomerInfo = async (
  purchases: any,
  rcPackage: RevenueCatPackage,
  email?: string | null,
): Promise<RevenueCatCustomerInfo> => {
  const cleanEmail = getCleanEmail(email);

  if (typeof purchases.setEmail === "function" && cleanEmail) {
    try {
      await purchases.setEmail(cleanEmail);
    } catch {
      // Optional.
    }
  }

  if (typeof purchases.purchase === "function") {
    const result = await purchases.purchase({
      rcPackage,
      customerEmail: cleanEmail || undefined,
    });

    return normalizeCustomerInfo(result);
  }

  if (typeof purchases.purchasePackage === "function") {
    const result = await purchases.purchasePackage(rcPackage);

    return normalizeCustomerInfo(result);
  }

  throw new Error("RevenueCat Kauf-Funktion wurde nicht gefunden.");
};

const customerHasEntitlement = (
  customerInfo: RevenueCatCustomerInfo,
  entitlementId: string,
): boolean => {
  return Boolean(customerInfo?.entitlements?.active?.[entitlementId]);
};

const customerHasFullWebsiteEntitlement = (
  customerInfo: RevenueCatCustomerInfo,
): boolean => {
  return customerHasEntitlement(customerInfo, FULL_WEBSITE_ENTITLEMENT_ID);
};

const customerHasCourseEntitlement = (
  customerInfo: RevenueCatCustomerInfo,
  courseLevel: RevenueCatCourseLevel,
): boolean => {
  return customerHasEntitlement(customerInfo, ENTITLEMENT_IDS[courseLevel]);
};

const checkWebsitePremiumAccess = async (
  appUserId: string,
  email?: string | null,
): Promise<boolean> => {
  const purchases = await configureRevenueCatWeb(appUserId);

  if (typeof purchases.isEntitledTo === "function") {
    try {
      return Boolean(await purchases.isEntitledTo(FULL_WEBSITE_ENTITLEMENT_ID));
    } catch {
      return false;
    }
  }

  const customerInfo = await purchases.getCustomerInfo();

  return customerHasFullWebsiteEntitlement(customerInfo);
};

const checkRevenueCatCourseAccess = async (
  appUserId: string,
  courseLevel: RevenueCatCourseLevel,
): Promise<boolean> => {
  const purchases = await configureRevenueCatWeb(appUserId);

  if (typeof purchases.isEntitledTo === "function") {
    try {
      return Boolean(await purchases.isEntitledTo(ENTITLEMENT_IDS[courseLevel]));
    } catch {
      return false;
    }
  }

  const customerInfo = await purchases.getCustomerInfo();

  return customerHasCourseEntitlement(customerInfo, courseLevel);
};

export const checkCourseAccess = async (
  appUserId: string,
  courseLevel: CourseAccessLevel,
  email?: string | null,
): Promise<boolean> => {
  const normalizedCourseLevel = normalizeCourseAccessLevel(courseLevel);

  if (!normalizedCourseLevel) {
    return false;
  }

  let backendHasAccess = false;

  if (email) {
    try {
      backendHasAccess = await checkBackendCourseAccess(
        email,
        normalizedCourseLevel,
      );
    } catch (error) {
      console.warn("[CourseAccess] Backend check failed:", error);
    }
  }

  if (backendHasAccess) {
    return true;
  }

  const hasWebsitePremiumAccess = await checkWebsitePremiumAccess(
    appUserId,
    email,
  );

  if (hasWebsitePremiumAccess) {
    return true;
  }

  if (!isRevenueCatCourseLevel(normalizedCourseLevel)) {
    return false;
  }

  return checkRevenueCatCourseAccess(appUserId, normalizedCourseLevel);
};

export const checkBookAccess = async (
  appUserId: string,
  bookLevel: CourseAccessLevel,
  email?: string | null,
): Promise<boolean> => {
  const normalizedBookLevel = normalizeCourseAccessLevel(bookLevel);

  if (!normalizedBookLevel) {
    return false;
  }

  if (email) {
    try {
      const backendAccess = await getBackendCourseAccess(
        email,
        null,
        false,
        normalizedBookLevel,
      );

      if (backendAccess.book_access_map[normalizedBookLevel]) {
        return true;
      }
    } catch (error) {
      console.warn("[BookAccess] Backend check failed:", error);
    }
  }

  const hasWebsitePremiumAccess = await checkWebsitePremiumAccess(
    appUserId,
    email,
  );

  if (hasWebsitePremiumAccess) {
    return true;
  }

  if (!isRevenueCatCourseLevel(normalizedBookLevel)) {
    return false;
  }

  return checkRevenueCatCourseAccess(appUserId, normalizedBookLevel);
};

export const getCourseAccessMap = async (
  appUserId: string,
  email?: string | null,
): Promise<CourseAccessMap> => {
  let backendMap = createAccessMap(false);

  if (email) {
    try {
      const backendAccess = await getBackendCourseAccess(email);

      if (backendAccess.has_record) {
        backendMap = backendAccess.course_access_map;
      }
    } catch (error) {
      console.warn("[CourseAccess] Backend access map failed:", error);
    }
  }

  let revenueCatMap = createAccessMap(false);

  try {
    const purchases = await configureRevenueCatWeb(appUserId);

    if (typeof purchases.isEntitledTo === "function") {
      const hasFullWebsiteAccess = await purchases.isEntitledTo(
        FULL_WEBSITE_ENTITLEMENT_ID,
      );

      if (hasFullWebsiteAccess) {
        revenueCatMap = createAccessMap(true);
      } else {
        const accessPairs = await Promise.all(
          REVENUECAT_COURSE_LEVELS.map(async (level) => {
            try {
              const hasAccess = await purchases.isEntitledTo(
                ENTITLEMENT_IDS[level],
              );

              return [level, Boolean(hasAccess)] as const;
            } catch {
              return [level, false] as const;
            }
          }),
        );

        accessPairs.forEach(([level, hasAccess]) => {
          revenueCatMap[level] = hasAccess;
        });
      }
    } else {
      const customerInfo = await purchases.getCustomerInfo();

      if (customerHasFullWebsiteEntitlement(customerInfo)) {
        revenueCatMap = createAccessMap(true);
      } else {
        REVENUECAT_COURSE_LEVELS.forEach((level) => {
          revenueCatMap[level] = customerHasCourseEntitlement(
            customerInfo,
            level,
          );
        });
      }
    }
  } catch (error) {
    console.warn("[CourseAccess] RevenueCat access map failed:", error);
  }

  return mergeAccessMaps(backendMap, revenueCatMap);
};

export const getBookAccessMap = async (
  appUserId: string,
  email?: string | null,
): Promise<BookAccessMap> => {
  let backendBookMap = createAccessMap(false);

  if (email) {
    try {
      const backendAccess = await getBackendCourseAccess(email);

      if (backendAccess.has_record) {
        backendBookMap = backendAccess.book_access_map;
      }
    } catch (error) {
      console.warn("[BookAccess] Backend book access map failed:", error);
    }
  }

  const courseOrWebsiteMap = await getCourseAccessMap(appUserId, email);

  return mergeAccessMaps(backendBookMap, courseOrWebsiteMap);
};

export const getAccessibleCourseLevels = async (
  appUserId: string,
  email?: string | null,
): Promise<CourseAccessLevel[]> => {
  const accessMap = await getCourseAccessMap(appUserId, email);

  return COURSE_ACCESS_LEVELS.filter((level) => accessMap[level]);
};

export const getAccessibleBookLevels = async (
  appUserId: string,
  email?: string | null,
): Promise<CourseAccessLevel[]> => {
  const accessMap = await getBookAccessMap(appUserId, email);

  return COURSE_ACCESS_LEVELS.filter((level) => accessMap[level]);
};

const isPurchaseCancelledMessage = (message: string): boolean => {
  const lowerMessage = message.toLowerCase();

  return (
    lowerMessage.includes("cancel") ||
    lowerMessage.includes("cancelled") ||
    lowerMessage.includes("canceled") ||
    lowerMessage.includes("abgebrochen")
  );
};

export const purchaseCourse = async ({
  appUserId,
  courseLevel,
  email,
}: PurchaseCourseParams): Promise<boolean> => {
  const purchases = await configureRevenueCatWeb(appUserId);
  const rcPackage = await findCoursePackage(purchases, courseLevel);

  try {
    const customerInfo = await purchasePackageAndReturnCustomerInfo(
      purchases,
      rcPackage,
      email,
    );

    if (
      customerHasFullWebsiteEntitlement(customerInfo) ||
      customerHasCourseEntitlement(customerInfo, courseLevel)
    ) {
      clearBackendCourseAccessCache();
      return true;
    }

    const latestCustomerInfo = await purchases.getCustomerInfo();
    const hasAccess =
      customerHasFullWebsiteEntitlement(latestCustomerInfo) ||
      customerHasCourseEntitlement(latestCustomerInfo, courseLevel);

    if (hasAccess) {
      clearBackendCourseAccessCache();
    }

    return hasAccess;
  } catch (error) {
    const message = getErrorMessage(error);

    if (isPurchaseCancelledMessage(message)) {
      throw new Error("Zahlung wurde abgebrochen.");
    }

    throw new Error(message);
  }
};

export const restoreCourseAccess = async (
  appUserId: string,
  courseLevel: CourseAccessLevel,
  email?: string | null,
): Promise<boolean> => {
  clearBackendCourseAccessCache();

  return checkCourseAccess(appUserId, courseLevel, email);
};

export const purchaseWebsitePremium = async ({
  appUserId,
  email,
}: PurchaseWebsitePremiumParams): Promise<boolean> => {
  const purchases = await configureRevenueCatWeb(appUserId);
  const rcPackage = await findWebsitePremiumPackage(purchases);

  try {
    const customerInfo = await purchasePackageAndReturnCustomerInfo(
      purchases,
      rcPackage,
      email,
    );

    if (customerHasFullWebsiteEntitlement(customerInfo)) {
      clearBackendCourseAccessCache();
      return true;
    }

    const latestCustomerInfo = await purchases.getCustomerInfo();
    const hasAccess = customerHasFullWebsiteEntitlement(latestCustomerInfo);

    if (hasAccess) {
      clearBackendCourseAccessCache();
    }

    return hasAccess;
  } catch (error) {
    const message = getErrorMessage(error);

    if (isPurchaseCancelledMessage(message)) {
      throw new Error("Zahlung wurde abgebrochen.");
    }

    throw new Error(message);
  }
};

export const restoreWebsitePremiumAccess = async (
  appUserId: string,
  email?: string | null,
): Promise<boolean> => {
  clearBackendCourseAccessCache();

  return checkWebsitePremiumAccess(appUserId, email);
};